import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import TypeAhead from '../TypeAhead';

/**
 * Reason options for a rolled booking — port of V1's `@tt_missedcutoff_reason`
 * (lib/defs.pl:3326), the same list the legacy `sub rolling` modal fed into its
 * RESTRICTED smod_box. Hardcoded in V1 (no table behind it), so it's hardcoded
 * here too; keep the two in sync if the legacy list ever changes.
 */
export const ROLLING_REASONS: ReadonlyArray<string> = [
  'ACCIDENT',
  'BOL DELAY',
  'CARRIER DISPATCH ERROR',
  'CARRIER SWITCH ERROR',
  'CREDIT HOLD',
  'CUSTOMER REQUEST',
  'CUT OFF ADVANCED',
  'DATA ENTRY ERROR',
  'DRIVER RELATED',
  'LATE ORDER',
  'MECHANICAL BREAKDOWN',
  'MISSED ORDER',
  'NOT DISPATCHED',
  'OVER DRIVER HOURS',
  'OVERBOOKED',
  'PRODUCT NOT READY',
  'SYSTEMS DOWN',
  'TERMINAL CONGESTION',
  'TRAFFIC',
  'TRUCK RELATED',
  'WAREHOUSE ISSUE',
  'WEATHER',
];

/**
 * The transient prompt values V1 posted as hidden form inputs. V1 also collected a
 * "Missed Cutoff?" Y/N — dropped here: `sr_rolling` assigns
 * $missedcutoffyesno and never reads it, and no column or report holds it, so the
 * dropdown only cost the user a click.
 */
export interface RollingReasonValues {
  rollingreason: string;
  rollingissue: string;
}

export interface RollingReasonModalProps {
  /** Pro being rolled — shown in the header for confirmation. */
  pronumber: number | string;
  /** The Rolling Fault the user just picked (echoed so they can see what they set). */
  rollingFault: string;
  /** Saving state — disables the buttons and shows "Saving…" while the save runs. */
  saving?: boolean;
  /** Cancel: caller clears Rolling Fault back to blank (V1 reset the dropdown). */
  onCancel: () => void;
  /** Confirm with both values: caller immediately saves the whole record. */
  onConfirm: (values: RollingReasonValues) => void;
}

/**
 * Rolled-booking prompt — port of V1 `sub rolling` (cms/lib/main.pl:12301,
 * DS2-1319 / DS2-3536). The legacy UI popped this from the Rolling Fault
 * dropdown's onchange (defs.pl:561) whenever a fault was picked while Booking
 * Hold was ROLLING, and its Save button set the hidden inputs then submitted the
 * whole edit form. `sr_rolling` reads them for the EXPORT ROLLING note and the
 * 'Export Rolling' history row.
 *
 * Both fields are required, matching the legacy Save guard's
 * "Reason/Issue for Rolling must be filled out".
 */
export function RollingReasonModal({
  pronumber,
  rollingFault,
  saving = false,
  onCancel,
  onConfirm,
}: RollingReasonModalProps) {
  const [reason, setReason] = useState('');
  const [issue, setIssue] = useState('');

  const isValid = reason.trim() !== '' && issue.trim() !== '';

  const handleConfirm = () => {
    if (!isValid || saving) return;
    onConfirm({
      rollingreason: reason.trim(),
      // V1's textarea had onBlur='upperit(this)' — the stored comment is upper case.
      rollingissue: issue.trim().toUpperCase(),
    });
  };

  // Alt+S / Alt+C, matching the footer chords on the Pro drawer behind us (and
  // V1's accesskey=s / accesskey=c on the same two buttons). Claiming the layer
  // is what stops Alt+S from reaching the drawer's Save while this is open.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys(
    {
      S: isValid && !saving ? handleConfirm : null,
      C: !saving ? onCancel : null,
    },
    rootRef,
    { layer: true },
  );

  // Mounted only while open, so the drag offset resets on unmount.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Reason for Rolling"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-scrim/50"
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
      >
        <ModalHeader
          title="Reason for Rolling"
          onClose={saving ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
          closeLabel="Cancel rolling fault"
        />

        <div className="px-6 py-4 space-y-3">
          <p className="text-label">
            Booking on <span className="font-semibold">{pronumber}</span> is rolled
            {rollingFault ? (
              <>
                {' '}with fault <span className="font-semibold">{rollingFault}</span>
              </>
            ) : null}
            . Both fields are required; saving commits the whole record.
          </p>

          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              Reason<span className="text-red-500 ml-1">*</span>
            </label>
            <TypeAhead
              options={ROLLING_REASONS.map((r) => ({ value: r, label: r }))}
              value={reason}
              onChange={setReason}
              onClear={() => setReason('')}
              placeholder="Select reason…"
              disabled={saving}
              ariaLabel="Reason for rolling"
              showSearch
              flagInvalidValue
            />
          </div>

          <div>
            <label
              className="block text-xs font-medium text-muted mb-1"
              htmlFor="rolling-comments"
            >
              Comments<span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id="rolling-comments"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-yellow-50 dark:bg-gray-700 text-body placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={5}
              placeholder="Enter comments for the rolled booking…"
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              // V1 upper-cased the comment on blur (upperit); same here so what
              // the user sees is what sr_rolling notes.
              onBlur={() => setIssue((v) => v.toUpperCase())}
              disabled={saving}
              aria-label="Comments"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid || saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default RollingReasonModal;
